import { useState } from 'react'
import type { Tarea, PrioridadTarea, EstadoAprobacion } from '@/lib/types'
import { formatDate } from '@/lib/utils'
import {
  IconArrowDown, IconArrowRight, IconArrowUp, IconAlertCircle,
  IconClock, IconMail, IconCheckCircle, IconXCircle,
  IconAlertTriangle, IconPaperclip, IconTimer, IconCalendar,
} from '@/components/icons/Icons'

interface TareaCardProps {
  tarea: Tarea
  evidenciaCount?: number
  onClick: () => void
  onDragStart: (e: React.DragEvent, tarea: Tarea) => void
  onApprove?: () => Promise<void>
}

const PRIORIDAD_CONFIG: Record<PrioridadTarea, { label: string; icon: React.ReactNode; className: string }> = {
  baja: { label: 'Baja', icon: <IconArrowDown size={12} />, className: 'priority-baja' },
  media: { label: 'Media', icon: <IconArrowRight size={12} />, className: 'priority-media' },
  alta: { label: 'Alta', icon: <IconArrowUp size={12} />, className: 'priority-alta' },
  urgente: { label: 'Urgente', icon: <IconAlertCircle size={12} />, className: 'priority-urgente' },
}

const APROBACION_BADGE: Record<EstadoAprobacion, { label: string; icon: React.ReactNode; color: string }> = {
  pendiente: { label: 'Pendiente', icon: <IconClock size={11} />, color: 'var(--text-muted)' },
  enviada: { label: 'Enviada', icon: <IconMail size={11} />, color: 'var(--info)' },
  aprobada: { label: 'Aprobada', icon: <IconCheckCircle size={11} />, color: 'var(--success)' },
  rechazada: { label: 'Rechazada', icon: <IconXCircle size={11} />, color: 'var(--danger)' },
}

export default function TareaCard({ tarea, evidenciaCount = 0, onClick, onDragStart, onApprove }: TareaCardProps) {
  const pConfig = PRIORIDAD_CONFIG[tarea.prioridad]
  const isOverdue = tarea.fecha_limite && new Date(tarea.fecha_limite + 'T23:59:59') < new Date() && tarea.estado !== 'completada'
  const [approving, setApproving] = useState(false)

  async function handleApprove(e: React.MouseEvent) {
    e.stopPropagation()
    if (!onApprove || approving) return
    setApproving(true)
    try { await onApprove() } finally { setApproving(false) }
  }

  return (
    <div
      className={`tarea-card ${pConfig.className}`}
      id={`tarea-card-${tarea.id}`}
      onClick={onClick}
      draggable
      onDragStart={e => onDragStart(e, tarea)}
    >
      <div className="tarea-card-header">
        <span className={`tarea-priority-badge ${pConfig.className}`}>
          {pConfig.icon} {pConfig.label}
        </span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {isOverdue && <span className="tarea-overdue-badge"><IconAlertTriangle size={11} /> Vencida</span>}
          {evidenciaCount > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '0.7rem', color: 'var(--text-muted)' }} title={`${evidenciaCount} evidencia(s)`}>
              <IconPaperclip size={11} /> {evidenciaCount}
            </span>
          )}
          {onApprove && (
            <button
              className="tarea-approve-btn"
              onClick={handleApprove}
              disabled={approving}
              title="Aprobar tarea"
              style={{ margin: 0 }}
            >
              <IconCheckCircle size={13} /> {approving ? '...' : 'Aprobar'}
            </button>
          )}
        </div>
      </div>

      <h4 className="tarea-card-title">{tarea.titulo}</h4>

      {tarea.descripcion && (
        <p className="tarea-card-desc">{tarea.descripcion.slice(0, 80)}{tarea.descripcion.length > 80 ? '...' : ''}</p>
      )}

      {/* Hours badge */}
      {(tarea.horas_estimadas > 0 || tarea.horas_reales > 0) && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
          <span style={{
            display: 'flex', alignItems: 'center', gap: 3,
            fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 10,
            background: 'color-mix(in srgb, var(--rol-principal) 15%, transparent)', color: 'var(--accent-primary)',
          }}>
            <IconTimer size={11} />
            {tarea.horas_reales > 0
              ? <>{tarea.horas_reales}h / {tarea.horas_estimadas}h</>
              : <>{tarea.horas_estimadas}h est.</>
            }
          </span>
          {tarea.tipo_cobro !== 'sin_costo' && tarea.estado_aprobacion && tarea.estado_aprobacion !== 'pendiente' && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 3,
              fontSize: '0.68rem', padding: '1px 6px', borderRadius: 8,
              color: APROBACION_BADGE[tarea.estado_aprobacion].color,
              border: `1px solid ${APROBACION_BADGE[tarea.estado_aprobacion].color}`,
            }}>
              {APROBACION_BADGE[tarea.estado_aprobacion].icon} {APROBACION_BADGE[tarea.estado_aprobacion].label}
            </span>
          )}
        </div>
      )}

      <div className="tarea-card-footer">
        {tarea.fecha_limite && (
          <span className={`tarea-card-date ${isOverdue ? 'overdue' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <IconCalendar size={11} /> {formatDate(tarea.fecha_limite)}
          </span>
        )}
        {tarea.etiquetas.length > 0 && (
          <div className="tarea-card-tags">
            {tarea.etiquetas.slice(0, 2).map(tag => (
              <span key={tag} className="tag-chip small">{tag}</span>
            ))}
            {tarea.etiquetas.length > 2 && (
              <span className="tag-chip small">+{tarea.etiquetas.length - 2}</span>
            )}
          </div>
        )}
      </div>


    </div>
  )
}
